"use client"

import * as React from "react"

import { toast } from "sonner"

import { CardBack, PICKS_MARK_PATH } from "@/components/cards/card-back"
import { CardFace } from "@/components/cards/card-face"
import { CardLamps, type LampProgram } from "@/components/cards/lamps"
import { Button } from "@/components/ui/button"
import { TelegramIcon, WhatsAppIcon, XTwitterIcon } from "@/components/ui/icons"
import { wheelAudio } from "@/components/wheel/wheel-audio"
import type { PlayerCardData } from "@/lib/display/player-card"
import { createSlipPickFromCard } from "@/lib/slip/slip-pick"
import { REELS, TILES_PER_REEL, type ReelTile } from "@/lib/slot/use-lit-reels"
import {
  selectAddPick,
  selectClearSlip,
  selectFlashPicks,
  selectHidePicks,
  selectLockPromoLineup,
  selectRevealPicks,
  useSlipStore,
} from "@/lib/store/slip-store"
import { cn } from "@/lib/utils"

/* =============================================================================
   Three drums of cards.

   The wheel's mass on three reels. Press Spin: whoosh, the room goes dark,
   the drums take torque one after another and cruise as a blur. Then,
   left to right, each drum comes down under constant friction, past its
   card and rocking back onto it. The last stop is the win: sound, lamps, sparks
   from the three windows. The three cards in the windows are a 3-pick
   lineup of LIT markets; Apply lineup lifts them out of their windows and
   flies them onto their rows in the real slip, promo-locked, the way the
   pack does.

   The house draws first: which card each drum will show is decided at the
   press, the drums only land on it. Transforms and opacity only.
   ============================================================================= */

const STAGE_W = 1000
const STAGE_H = 600
const WINDOW_W = 260
const WINDOW_H = 430
const WINDOW_GAP = 30
const CARD_W = 224
const CARD_H = CARD_W * 1.4
/** Pitch between cards on a drum. */
const PITCH = CARD_H + 18
const WINDOWS_LEFT = (STAGE_W - (REELS * WINDOW_W + (REELS - 1) * WINDOW_GAP)) / 2
const WINDOW_TOP = 40

/* Drum motion: tiles per second, tiles per second squared. */
const CRUISE = 11
const TORQUE_S = 0.7
/** Two-stage brake: friction takes the speed off, then a crawl over the
    last card and a half where every edge passes on its own, and the eye
    cannot tell which card will stay. */
const FRICTION = 4.2
const CRAWL_V = 1.1
const CRAWL_FRICTION = 0.42
/** Cruise to rest, in tiles. */
const BRAKE_DIST = (CRUISE * CRUISE - CRAWL_V * CRAWL_V) / (2 * FRICTION) + (CRAWL_V * CRAWL_V) / (2 * CRAWL_FRICTION)
/** Rock past the card and back. */
const SPRING_K = 90
const SPRING_C = 13
const STAGGER_MS = 140
/** The drums stop by themselves, left to right. */
const AUTO_STOP_MS = [2200, 3700, 5200]

/* The flight into the slip, the pack's numbers. */
const LIFT_MS = 300
const FLY_MS = 780
const FLY_STAGGER_MS = 520
const FLY_SCALE = 0.3
const LAND_SPARKS = 14
const WIN_SPARKS = 16

const SHARE_URL = "https://go.getpicks.app/slot"

type Phase = "idle" | "spinning" | "done" | "flying" | "applied"

interface Drum {
  /** Continuous position in tiles; the card at the window is round(pos). */
  pos: number
  v: number
  state: "rest" | "torque" | "cruise" | "brake" | "rock" | "still"
  target: number
  /** Where braking must begin so the drum dies on target + overshoot. */
  brakeAt: number
  lastTile: number
  startedAt: number
}

function ShareCircle({ label, onClick, children }: { label: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="flex size-11 items-center justify-center rounded-full bg-grey-800 text-white transition-colors hover:bg-grey-700"
    >
      {children}
    </button>
  )
}

function ShareRow() {
  const text = "I just lined up 3 LIT picks on Picks."
  function openChannel(channel: "x" | "telegram" | "whatsapp") {
    const links: Record<typeof channel, string> = {
      x: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(SHARE_URL)}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(SHARE_URL)}&text=${encodeURIComponent(text)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(`${text} ${SHARE_URL}`)}`,
    }
    window.open(links[channel], "_blank", "noopener,noreferrer")
  }
  function copy() {
    navigator.clipboard.writeText(`${text} ${SHARE_URL}`).then(
      () => toast.success("Link copied"),
      () => toast.error("Could not copy")
    )
  }
  return (
    <div className="flex items-center gap-2">
      <ShareCircle label="X" onClick={() => openChannel("x")}>
        <XTwitterIcon className="size-4" />
      </ShareCircle>
      <ShareCircle label="Telegram" onClick={() => openChannel("telegram")}>
        <TelegramIcon className="size-[18px]" />
      </ShareCircle>
      <ShareCircle label="WhatsApp" onClick={() => openChannel("whatsapp")}>
        <WhatsAppIcon className="size-[18px]" />
      </ShareCircle>
      <Button type="button" variant="outline" onClick={copy} className="ml-1 h-11 rounded-full px-5">
        Copy link
      </Button>
    </div>
  )
}

/** A filler on the drum: the card back's plate struck in brand pink. Same
    thickness, the frame pressed in, halftone fading across, the plate
    darkening to its edges, the foot band, and the mark printed in relief
    with the shadow the raised print throws. Depth from shade, no light on it. */
function FillerCard({ width }: { width: number }) {
  const id = React.useId()
  const fillId = `${id}-fill`
  return (
    <div
      className="relative isolate overflow-hidden rounded-[18px] ring-1 ring-inset ring-white-200"
      style={{
        width,
        height: width * 1.4,
        // The pink plate, a shade deeper toward the lower right.
        background: "linear-gradient(118deg, hsl(var(--primary-0)) 0%, hsl(var(--primary)) 55%, hsl(335 83% 42%) 100%)",
        boxShadow: "inset 0 -2px 0 hsl(var(--black-0) / 0.6), inset 0 1px 0 hsl(var(--white-0) / 0.16)",
      }}
    >
      {/* Halftone, fading across the plate. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: "radial-gradient(rgba(255,255,255,0.7) 1px, transparent 1.4px)",
          backgroundSize: "7px 7px",
          opacity: 0.22,
          mixBlendMode: "overlay",
          maskImage: "linear-gradient(to right, black 0%, transparent 80%)",
          WebkitMaskImage: "linear-gradient(to right, black 0%, transparent 80%)",
        }}
      />
      {/* Shade: the plate darkens toward its edges, so the middle stands proud. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(90% 80% at 50% 48%, transparent 40%, hsl(var(--black-0) / 0.26) 80%, hsl(var(--black-0) / 0.5) 100%)",
        }}
      />
      {/* The frame, pressed into the plate. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-[12px] top-[12px] bottom-[30px] rounded-[10px]"
        style={{ boxShadow: "inset 0 1px 0 hsl(var(--white-0) / 0.22), inset 0 -1px 0 hsl(var(--white-0) / 0.06), 0 1px 0 hsl(var(--black-0) / 0.5)" }}
      />
      <span aria-hidden className="pointer-events-none absolute inset-x-[12px] top-[12px] bottom-[30px] rounded-[10px] ring-1 ring-inset ring-white-300" />
      <span aria-hidden className="pointer-events-none absolute inset-x-[16px] top-[16px] bottom-[34px] rounded-[7px] ring-1 ring-inset ring-white-200" />
      {/* The foot band. */}
      <span className="pointer-events-none absolute inset-x-0 bottom-[10px] text-center font-open-sauce text-[9px] font-semibold tracking-tight text-white/55">
        go.getpicks.app
      </span>
      {/* The mark in relief: the shadow it throws a touch below, a brushed
          white fill lighter at the top, a hairline along its edges. */}
      <svg viewBox="0 0 42 34" aria-hidden className="absolute left-1/2 top-[calc(50%-9px)] w-[46%] -translate-x-1/2 -translate-y-1/2">
        <defs>
          <linearGradient id={fillId} x1="0" y1="0" x2="0.2" y2="1">
            <stop offset="0" stopColor="hsl(var(--white-0))" />
            <stop offset="0.55" stopColor="hsl(0 0% 92%)" />
            <stop offset="1" stopColor="hsl(0 0% 80%)" />
          </linearGradient>
        </defs>
        <g transform="translate(0.65 1.5)">
          <path d={PICKS_MARK_PATH} fill="hsl(var(--black-0))" fillOpacity={0.45} transform="translate(0 1.6)" />
          <path d={PICKS_MARK_PATH} fill={`url(#${fillId})`} />
          <path d={PICKS_MARK_PATH} fill="none" stroke="hsl(var(--white-0))" strokeOpacity={0.5} strokeWidth={0.35} />
        </g>
      </svg>
    </div>
  )
}

/** Sparks thrown from a point. */
function Burst({ x, y, count }: { x: number; y: number; count: number }) {
  return (
    <span aria-hidden className="pointer-events-none absolute left-0 top-0 z-[90]" style={{ transform: `translate(${x}px, ${y}px)` }}>
      {Array.from({ length: count }, (_, k) => {
        const ang = (k / count) * Math.PI * 2 + (k % 2) * 0.25
        const dist = 70 + (k % 3) * 30
        return (
          <span
            key={k}
            className="cards-spark absolute left-0 top-0 rounded-full"
            style={{
              width: k % 3 === 0 ? 5 : 3,
              height: k % 3 === 0 ? 5 : 3,
              background: k % 4 === 0 ? "hsl(335 83% 82%)" : "hsl(var(--white-0))",
              ["--dx" as string]: `${Math.cos(ang) * dist}px`,
              ["--dy" as string]: `${Math.sin(ang) * dist}px`,
              animationDelay: `${(k * 19) % 120}ms`,
            }}
          />
        )
      })}
    </span>
  )
}

/* The lever: the cards' slide, stood upright. A pink box, a white knob;
   drag the knob to the bottom and the drums throw. It springs back up. */
const LEVER_W = 56
const LEVER_H = 320
const KNOB = 48
const LEVER_PAD = (LEVER_W - KNOB) / 2
const LEVER_RUN = LEVER_H - KNOB - LEVER_PAD * 2

function Lever({ armed, onPull }: { armed: boolean; onPull: () => void }) {
  const [y, setY] = React.useState(0)
  const [dragging, setDragging] = React.useState(false)
  const startRef = React.useRef(0)
  const firedRef = React.useRef(false)
  const [burst, setBurst] = React.useState(0)

  const onPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!armed) return
    e.currentTarget.setPointerCapture(e.pointerId)
    startRef.current = e.clientY - y
    firedRef.current = false
    setDragging(true)
  }
  const onPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!dragging) return
    // The last 15% of the travel is stiff: the knob gives a third of what
    // the hand gives, so the throw is pushed through, not slid into.
    const raw = Math.max(0, e.clientY - startRef.current)
    const stiffFrom = LEVER_RUN * 0.85
    const next = Math.min(LEVER_RUN, raw <= stiffFrom ? raw : stiffFrom + (raw - stiffFrom) * 0.34)
    setY(next)
    if (next >= LEVER_RUN * 0.985 && !firedRef.current) {
      // The throw fires at the bottom of the pull, not on release.
      firedRef.current = true
      wheelAudio.tick(1)
      setBurst(Date.now())
      onPull()
    }
  }
  const onPointerUp = () => {
    if (!dragging) return
    setDragging(false)
    setY(0)
  }
  const p = y / LEVER_RUN
  const seatY = LEVER_H - LEVER_PAD - KNOB / 2

  return (
    <div className="relative" style={{ width: LEVER_W, height: LEVER_H }}>
    <div
      role="slider"
      aria-label="Pull to spin"
      aria-orientation="vertical"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(p * 100)}
      className={cn("relative select-none overflow-hidden rounded-full bg-primary transition-opacity", !armed && "opacity-40")}
      style={{ width: LEVER_W, height: LEVER_H, touchAction: "none" }}
    >
      {/* The seat at the bottom: the mark pressed into the pill, where the
          knob lands. */}
      <svg
        aria-hidden
        viewBox="0 0 40.7 31"
        className="pointer-events-none absolute left-1/2 -translate-x-1/2 -translate-y-1/2"
        style={{ top: seatY, width: 26, height: 20 }}
      >
        <path d={PICKS_MARK_PATH} fill="hsl(var(--white-0))" fillOpacity={0.35} transform="translate(0 1)" />
        <path d={PICKS_MARK_PATH} fill="hsl(335 83% 30%)" />
      </svg>
      <button
        type="button"
        aria-hidden
        tabIndex={-1}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        className="absolute left-1/2 flex -translate-x-1/2 items-center justify-center rounded-full bg-white text-primary shadow-[0_2px_8px_rgba(0,0,0,0.35)]"
        style={{
          top: LEVER_PAD,
          width: KNOB,
          height: KNOB,
          transform: `translate(-50%, ${y}px)`,
          transition: dragging ? "none" : "transform 520ms cubic-bezier(0.34, 1.56, 0.64, 1)",
          cursor: !armed ? "default" : dragging ? "grabbing" : "grab",
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 5v14M6 13l6 6 6-6" />
        </svg>
      </button>
    </div>
    {/* The hit: sparks off the seat. */}
    {burst > 0 && <Burst key={burst} x={LEVER_W / 2} y={seatY} count={14} />}
    </div>
  )
}

export interface SlotMachineProps {
  reels: ReelTile[][]
  /** The drums are dealt. Until then the windows show card backs and the
      lever is dead. */
  ready: boolean
  /** The last card has landed in the slip. */
  onApplied?: () => void
  className?: string
}

export function SlotMachine({ reels, ready, onApplied, className }: SlotMachineProps) {
  const [phase, setPhase] = React.useState<Phase>("idle")
  const [lamps, setLamps] = React.useState<LampProgram>("off")
  const [bursts, setBursts] = React.useState<{ key: number; x: number; y: number; count: number }[]>([])
  /** The flight: each window's card, lifting then swooping. */
  const [flyers, setFlyers] = React.useState<("seat" | "lift" | "slip" | "gone")[]>(["seat", "seat", "seat"])
  const [targets, setTargets] = React.useState<Record<number, { x: number; y: number }>>({})
  const [dimmed, setDimmed] = React.useState(false)

  const addPick = useSlipStore(selectAddPick)
  const flashPicks = useSlipStore(selectFlashPicks)
  const hidePicks = useSlipStore(selectHidePicks)
  const revealPicks = useSlipStore(selectRevealPicks)
  const clearSlip = useSlipStore(selectClearSlip)
  const lockPromoLineup = useSlipStore(selectLockPromoLineup)

  const stageRef = React.useRef<HTMLDivElement>(null)
  const tileRefs = React.useRef<(HTMLDivElement | null)[][]>([])
  const drums = React.useRef<Drum[]>(
    Array.from({ length: REELS }, () => ({ pos: 0, v: 0, state: "rest", target: 0, brakeAt: 0, lastTile: 0, startedAt: 0 }))
  )
  const timers = React.useRef<number[]>([])
  const later = React.useCallback((fn: () => void, ms: number) => {
    const t = window.setTimeout(fn, ms)
    timers.current.push(t)
    return t
  }, [])
  React.useEffect(() => () => timers.current.forEach((t) => window.clearTimeout(t)), [])
  React.useEffect(() => wheelAudio.preload(), [])
  const onApplyRef = React.useRef(onApplied)
  onApplyRef.current = onApplied

  const tilesOf = (i: number) => reels[i]?.length ?? TILES_PER_REEL

  /** The house's draw: one tile per drum. One time in four, when the drums
      allow it, three from one match. */
  const draw = React.useCallback((): number[] => {
    const byEvent = new Map<string, number[]>()
    reels.forEach((reel, i) =>
      reel.forEach((tile, k) => {
        const id = tile.live?.eventId
        if (!id) return
        const row = byEvent.get(id) ?? Array(REELS).fill(-1)
        if (row[i] < 0) row[i] = k
        byEvent.set(id, row)
      })
    )
    const lines = [...byEvent.values()].filter((row) => row.every((k) => k >= 0))
    if (lines.length > 0 && Math.random() < 0.25) return lines[Math.floor(Math.random() * lines.length)]
    // Fillers are never drawn.
    return reels.map((reel) => {
      const real = reel.map((t, k) => (t.filler ? -1 : k)).filter((k) => k >= 0)
      return real[Math.floor(Math.random() * real.length)] ?? 0
    })
  }, [reels])

  const drawRef = React.useRef<number[]>([0, 0, 0])

  /* ---- The drums' loop ---- */
  React.useEffect(() => {
    let raf = 0
    let last = performance.now()
    const frame = (now: number) => {
      raf = requestAnimationFrame(frame)
      const dt = Math.min(0.05, (now - last) / 1000)
      last = now
      drums.current.forEach((d, i) => {
        const n = tilesOf(i)
        if (d.state === "rest" || d.state === "still") {
          placeTiles(i, d.pos)
          return
        }
        if (d.state === "torque") {
          d.v = Math.min(CRUISE, d.v + (CRUISE / TORQUE_S) * dt)
          if (d.v >= CRUISE) d.state = "cruise"
        }
        if (d.state === "brake") {
          d.v = Math.max(0, d.v - (d.v > CRAWL_V ? FRICTION : CRAWL_FRICTION) * dt)
          if (d.v <= 0.02 || d.pos >= d.brakeAt + BRAKE_DIST) {
            d.state = "rock"
            d.v = Math.max(0, d.v)
          }
        }
        if (d.state === "rock") {
          // A damped spring onto the target from the overshoot.
          const x = d.pos - d.target
          const a = -SPRING_K * x - SPRING_C * d.v
          d.v += a * dt
          d.pos += d.v * dt
          if (Math.abs(x) < 0.0015 && Math.abs(d.v) < 0.01) {
            d.pos = d.target
            d.v = 0
            d.state = "still"
          }
        } else {
          d.pos += d.v * dt
        }
        // Ticks: a fret passing.
        const tile = Math.floor(d.pos + 0.5)
        if (tile !== d.lastTile) {
          d.lastTile = tile
          wheelAudio.tick(Math.min(1, d.v / CRUISE) * 0.9)
        }
        placeTiles(i, d.pos)
      })
    }
    /** Every card of drum i on its own, from the drum's position: each is
        a small layer of its own, only the ones at the window are drawn, and
        the wrap is a modulo per card. The drum turns toward the player:
        cards enter at the top and leave at the bottom. */
    function placeTiles(i: number, pos: number) {
      const n = tilesOf(i)
      const shown = ((pos % n) + n) % n
      const els = tileRefs.current[i] ?? []
      for (let k = 0; k < n; k++) {
        const el = els[k]
        if (!el) continue
        let dTiles = ((k - shown) % n + n) % n
        if (dTiles > n / 2) dTiles -= n
        const near = Math.abs(dTiles) < 1.6
        el.style.visibility = near ? "visible" : "hidden"
        if (near) el.style.transform = `translate3d(0, ${-dTiles * PITCH}px, 0)`
      }
    }
    raf = requestAnimationFrame(frame)
    return () => cancelAnimationFrame(raf)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reels])

  /** Send drum i to its drawn tile: cruise on until the braking point that
      dies exactly a little past the target, then rock back. */
  const stopDrum = React.useCallback(
    (i: number) => {
      const d = drums.current[i]
      if (d.state !== "cruise" && d.state !== "torque") return
      const n = tilesOf(i)
      const targetTile = drawRef.current[i]
      const brakeDist = BRAKE_DIST
      const overshoot = 0.08
      // The first pass of the target that leaves room to brake.
      let target = Math.ceil(d.pos)
      while ((((target % n) + n) % n) !== targetTile || target - d.pos < brakeDist + 0.6) target += 1
      d.target = target
      d.brakeAt = target + overshoot - brakeDist
      // Brake begins when pos reaches brakeAt; watched here.
      const watch = () => {
        if (d.state === "cruise" && d.pos >= d.brakeAt) {
          d.state = "brake"
          return
        }
        if (d.state === "cruise" || d.state === "torque") requestAnimationFrame(watch)
      }
      requestAnimationFrame(watch)
      wheelAudio.tick(1)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reels]
  )

  /** All three still: the win. */
  const settledRef = React.useRef(0)
  React.useEffect(() => {
    if (phase !== "spinning") return
    const id = window.setInterval(() => {
      // Every drum ran this spin and has come to rest.
      if (drums.current.every((d) => d.state === "still" && d.startedAt > 0)) {
        window.clearInterval(id)
        const line = drawRef.current
        const events = line.map((k, i) => reels[i]?.[k]?.live?.eventId)
        const oneMatch = events.every((e) => e && e === events[0])
        wheelAudio.win(oneMatch)
        setLamps("wave")
        setBursts((b) => [
          ...b,
          ...line.map((_, i) => ({
            key: Date.now() + i,
            x: WINDOWS_LEFT + i * (WINDOW_W + WINDOW_GAP) + WINDOW_W / 2,
            y: WINDOW_TOP + WINDOW_H / 2,
            count: WIN_SPARKS,
          })),
        ])
        settledRef.current = Date.now()
        setPhase("done")
      }
    }, 40)
    return () => window.clearInterval(id)
  }, [phase, reels])

  const spin = () => {
    if (phase !== "idle") return
    wheelAudio.unlock()
    wheelAudio.start()
    drawRef.current = draw()
    // The drums are at rest again before anything reads them: a drum still
    // marked "still" from the last spin would read as already stopped.
    drums.current.forEach((d) => {
      d.state = "rest"
      d.v = 0
      d.startedAt = 0
    })
    setDimmed(true)
    setPhase("spinning")
    setLamps("chase")
    drums.current.forEach((d, i) => {
      later(() => {
        d.state = "torque"
        d.startedAt = performance.now()
        d.lastTile = Math.floor(d.pos + 0.5)
      }, 500 + i * STAGGER_MS)
    })
    AUTO_STOP_MS.forEach((ms, i) => later(() => stopDrum(i), 500 + ms))
  }

  /** Stage-space centre of the slip row for a market. The slip may still be
      sliding in, so the row is placed where the slip will rest. */
  const rowSpot = (marketBaseId: string | undefined, i: number) => {
    const stage = stageRef.current?.getBoundingClientRect()
    if (!stage) return { x: 0, y: 0 }
    const slip = document.querySelector<HTMLElement>("[data-cards-slip]")
    const row = marketBaseId ? slip?.querySelector(`[data-pick-id="${marketBaseId}"]`) : null
    const r = (row ?? null)?.getBoundingClientRect()
    if (r && slip) {
      const a = slip.getBoundingClientRect()
      const restLeft = window.innerWidth - a.width
      const restTop = parseFloat(getComputedStyle(slip).top) || a.top
      const rowLeft = restLeft + (r.left - a.left)
      const rowTop = restTop + (r.top - a.top)
      const group = row?.closest("[data-slot='slip-group']") as HTMLElement | null
      const groupFolded = group ? group.getBoundingClientRect().height < 4 : false
      const opens = r.height < 4 ? 100 : r.height
      const headerAbove = groupFolded ? 56 : 0
      return { x: rowLeft + r.width * 0.42 - stage.left, y: rowTop + headerAbove + opens / 2 - stage.top }
    }
    const sr = slip?.getBoundingClientRect()
    return sr ? { x: sr.left + 150 - stage.left, y: sr.top + 140 + i * 64 - stage.top } : { x: stage.width - 80, y: 120 + i * 64 }
  }

  const apply = () => {
    if (phase !== "done") return
    setPhase("flying")
    setLamps("chase")
    wheelAudio.unlock()
    wheelAudio.start()
    clearSlip()
    lockPromoLineup()
    const line = drawRef.current
    const sides = line.map((k, i) => {
      const card = reels[i]?.[k]?.live
      if (!card) return "more" as const
      return card.outcomeMode === "less-only" ? ("less" as const) : card.outcomeMode === "more-only" ? ("more" as const) : Math.random() < 0.5 ? ("less" as const) : ("more" as const)
    })
    for (let i = 0; i < REELS; i++) {
      const t0 = 250 + i * FLY_STAGGER_MS
      later(() => setFlyers((f) => f.map((s, k) => (k === i ? "lift" : s))), t0)
      later(() => {
        const card: PlayerCardData | undefined = reels[i]?.[line[i]]?.live
        if (card) {
          hidePicks([card.marketBaseId])
          addPick(createSlipPickFromCard(card, sides[i]))
        }
        requestAnimationFrame(() =>
          requestAnimationFrame(() => {
            setTargets((prev) => ({ ...prev, [i]: rowSpot(card?.marketBaseId, i) }))
            setFlyers((f) => f.map((s, k) => (k === i ? "slip" : s)))
            wheelAudio.swish()
          })
        )
      }, t0 + LIFT_MS)
      later(() => {
        const card = reels[i]?.[line[i]]?.live
        if (card) {
          revealPicks([card.marketBaseId])
          flashPicks([card.marketBaseId])
        }
        setTargets((prev) => {
          const at = prev[i]
          if (at) setBursts((b) => [...b, { key: Date.now() + i, x: at.x, y: at.y, count: LAND_SPARKS }])
          return prev
        })
        setFlyers((f) => f.map((s, k) => (k === i ? "gone" : s)))
        wheelAudio.tick(0.9)
      }, t0 + LIFT_MS + 40 + FLY_MS)
    }
    later(() => {
      setPhase("applied")
      setLamps("kick")
      onApplyRef.current?.()
    }, 250 + REELS * FLY_STAGGER_MS + LIFT_MS + FLY_MS + 600)
  }

  const again = () => {
    timers.current.forEach((t) => window.clearTimeout(t))
    timers.current = []
    setFlyers(["seat", "seat", "seat"])
    setTargets({})
    setBursts([])
    setLamps("off")
    setPhase("idle")
  }

  const windowX = (i: number) => WINDOWS_LEFT + i * (WINDOW_W + WINDOW_GAP)
  const cardX = (i: number) => windowX(i) + (WINDOW_W - CARD_W) / 2
  const cardY = WINDOW_TOP + (WINDOW_H - CARD_H) / 2

  const flyerTransform = (i: number) => {
    const s = flyers[i]
    if (s === "lift") return `translate(${cardX(i)}px, ${cardY - 120}px) scale(1)`
    if (s === "slip" || s === "gone") {
      const t = targets[i] ?? { x: STAGE_W - 80, y: 120 }
      return `translate(${t.x - CARD_W / 2}px, ${t.y - CARD_H / 2}px) scale(${FLY_SCALE})`
    }
    return `translate(${cardX(i)}px, ${cardY}px) scale(1)`
  }

  return (
    <div className={cn("relative", className)}>
      {/* The room goes dark round the machine while the drums run. */}
      <div
        aria-hidden
        className={cn(
          "pointer-events-none fixed inset-0 z-[305] opacity-0 motion-reduce:hidden",
          phase === "spinning" || phase === "flying" ? "cards-dim-in" : dimmed ? "cards-dim-out" : ""
        )}
        style={{
          background:
            "radial-gradient(ellipse 50% 44% at 50% 46%, transparent 0%, transparent 62%, hsl(var(--black-0) / 0.7) 84%, hsl(var(--black-0) / 1) 100%)",
        }}
      />

      <div ref={stageRef} className="relative z-[306] mx-auto select-none" style={{ width: STAGE_W, height: STAGE_H }}>
        <CardLamps
          program={lamps}
          stageW={STAGE_W}
          topY={WINDOW_TOP - 26}
          bottomY={WINDOW_TOP + WINDOW_H + 26}
          halfW={(REELS * WINDOW_W + (REELS - 1) * WINDOW_GAP) / 2 + 16}
          topSag={0}
          bottomSag={0}
        />

        {/* The three windows. Each is a drum: the cards on a strip that the
            loop moves, one copy either side for the wrap, the glass darkening
            top and bottom where the drum curves away. */}
        {reels.map((reel, i) => {
          return (
            <div
              key={i}
              className="absolute overflow-hidden rounded-[22px] bg-grey-950"
              style={{
                left: windowX(i),
                top: WINDOW_TOP,
                width: WINDOW_W,
                height: WINDOW_H,
                boxShadow: "inset 0 0 0 1px hsl(var(--white-100)), inset 0 18px 30px hsl(var(--black-0) / 0.7), inset 0 -18px 30px hsl(var(--black-0) / 0.7), 0 24px 40px hsl(var(--black-0) / 0.6)",
              }}
            >
              {!ready && (
                <div className="absolute" style={{ left: (WINDOW_W - CARD_W) / 2, top: (WINDOW_H - CARD_H) / 2 }}>
                  <CardBack width={CARD_W} />
                </div>
              )}
              {reel.map((tile, k) => (
                <div
                  key={k}
                  ref={(el) => {
                    ;(tileRefs.current[i] ??= [])[k] = el
                  }}
                  className="absolute will-change-transform"
                  style={{
                    left: (WINDOW_W - CARD_W) / 2,
                    top: (WINDOW_H - CARD_H) / 2,
                    width: CARD_W,
                    height: CARD_H,
                    visibility: "hidden",
                    contain: "paint",
                    // The drawn card leaves its window for the flight.
                    opacity: k === drawRef.current[i] && flyers[i] !== "seat" ? 0 : 1,
                  }}
                >
                  {tile.filler ? <FillerCard width={CARD_W} /> : <CardFace data={tile.face} width={CARD_W} tag={tile.live ? "lit" : null} eager />}
                </div>
              ))}
              {/* The glass: the drum's curve, darker away from the window's middle. */}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0"
                style={{
                  background:
                    "linear-gradient(180deg, hsl(var(--black-0) / 0.75) 0%, transparent 22%, transparent 78%, hsl(var(--black-0) / 0.75) 100%)",
                }}
              />
              {/* The stop under the window. */}
            </div>
          )
        })}

        {/* The flyers: the three drawn cards on their way to the slip. */}
        {phase === "flying" || phase === "applied"
          ? reels.map((reel, i) => {
              const tile = reel[drawRef.current[i]]
              if (!tile) return null
              return (
                <div
                  key={i}
                  className="absolute left-0 top-0"
                  style={{
                    width: CARD_W,
                    height: CARD_H,
                    transformOrigin: "50% 50%",
                    transform: flyerTransform(i),
                    opacity: flyers[i] === "gone" || flyers[i] === "slip" ? 0 : 1,
                    transition:
                      flyers[i] === "lift"
                        ? `transform ${LIFT_MS}ms cubic-bezier(0.34, 1.56, 0.64, 1)`
                        : `transform ${FLY_MS}ms cubic-bezier(0.6, 0, 0.2, 1), opacity 220ms ease ${FLY_MS - 220}ms`,
                    zIndex: 80,
                  }}
                >
                  {tile.filler ? <FillerCard width={CARD_W} /> : <CardFace data={tile.face} width={CARD_W} tag={tile.live ? "lit" : null} eager />}
                </div>
              )
            })
          : null}

        {bursts.map((b) => (
          <Burst key={b.key} x={b.x} y={b.y} count={b.count} />
        ))}


        {/* The lever, on the cabinet's right side. */}
        <div className="absolute" style={{ left: WINDOWS_LEFT + REELS * WINDOW_W + (REELS - 1) * WINDOW_GAP + 28, top: WINDOW_TOP + (WINDOW_H - LEVER_H) / 2 }}>
          <Lever armed={ready && phase === "idle"} onPull={spin} />
        </div>

        {phase === "done" && (
          <div className="absolute left-1/2 flex -translate-x-1/2 flex-col items-center gap-3 cards-actions-in" style={{ top: WINDOW_TOP + WINDOW_H + 52 }}>
            <div className="flex items-center gap-4">
              <Button type="button" onClick={apply} className="h-11 rounded-full px-6">
                Apply lineup
              </Button>
              <ShareRow />
              <Button type="button" variant="ghost" onClick={again} className="h-11 rounded-full px-5 text-grey-400 hover:text-white">
                Spin again
              </Button>
            </div>
          </div>
        )}

        {phase === "applied" && !onApplied && (
          <div className="absolute left-1/2 flex -translate-x-1/2 flex-col items-center gap-3 cards-actions-in" style={{ top: WINDOW_TOP + WINDOW_H + 52 }}>
            <Button type="button" variant="ghost" onClick={again} className="h-11 rounded-full px-5 text-grey-400 hover:text-white">
              Spin again
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
